import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DOMPurify from "dompurify";
import { 
  CalendarDays, Users, TrendingUp, AlertTriangle, ShoppingCart, 
  CheckCircle, XCircle, Phone, Clock, Sparkles, Target, 
  MessageCircle, Heart, Star, Package, ArrowRight
} from "lucide-react";

/* Sanitize + apply markdown-like formatting to text */
function sanitizeAndFormat(text) {
  const formatted = text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')
    .replace(/(£[\d,.]+)/g, '<strong class="text-primary font-semibold">$1</strong>')
    .replace(/(\d+%)/g, '<strong class="text-primary font-semibold">$1</strong>')
    .replace(/(\d+ days?)/gi, '<strong>$1</strong>');
  return DOMPurify.sanitize(formatted, { ALLOWED_TAGS: ['strong', 'em', 'b', 'i'], ALLOWED_ATTR: ['class'] });
}

// Map section keywords to icons and colors
const SECTION_ICONS = {
  'appointment': { icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-100' },
  'booking': { icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-100' },
  'today': { icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-100' },
  'schedule': { icon: CalendarDays, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-100' },
  'upcoming': { icon: Clock, color: 'text-indigo-500', bg: 'bg-indigo-50 border-indigo-100' },
  'cancel': { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-100' },
  'no-show': { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 border-red-100' },
  'rebook': { icon: Phone, color: 'text-orange-500', bg: 'bg-orange-50 border-orange-100' },
  'retention': { icon: Users, color: 'text-purple-500', bg: 'bg-purple-50 border-purple-100' },
  'client': { icon: Users, color: 'text-purple-500', bg: 'bg-purple-50 border-purple-100' },
  'revenue': { icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-50 border-green-100' },
  'finance': { icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-50 border-green-100' },
  'profit': { icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-50 border-green-100' },
  'sales': { icon: TrendingUp, color: 'text-green-500', bg: 'bg-green-50 border-green-100' },
  'inventory': { icon: Package, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-100' },
  'stock': { icon: Package, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-100' },
  'action': { icon: Target, color: 'text-pink-500', bg: 'bg-pink-50 border-pink-100' },
  'suggest': { icon: Sparkles, color: 'text-pink-500', bg: 'bg-pink-50 border-pink-100' },
  'recommend': { icon: Sparkles, color: 'text-pink-500', bg: 'bg-pink-50 border-pink-100' },
  'risk': { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50 border-red-100' },
  'warning': { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' },
  'win': { icon: Star, color: 'text-green-500', bg: 'bg-green-50 border-green-100' },
  'vip': { icon: Heart, color: 'text-pink-500', bg: 'bg-pink-50 border-pink-100' },
  'service': { icon: Sparkles, color: 'text-primary', bg: 'bg-secondary border-border' },
  'promo': { icon: Star, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-100' },
  'order': { icon: ShoppingCart, color: 'text-blue-500', bg: 'bg-blue-50 border-blue-100' },
};

function getIconForHeading(text) {
  const lower = text.toLowerCase();
  for (const [key, val] of Object.entries(SECTION_ICONS)) {
    if (lower.includes(key)) return val;
  }
  return { icon: Sparkles, color: 'text-primary', bg: 'bg-secondary border-border' };
}

function highlightNumbers(text) {
  // Bold numbers with £ or % or counts
  return text.replace(/(£[\d,.]+|\d+%|\d+ appointment|\d+ client|\d+ day)/gi, (match) => `**${match}**`);
}

/**
 * Rich AI Content Renderer
 * Parses AI markdown and renders with icons, cards, and styled sections
 */
export function AIContentRenderer({ content, compact = false }) {
  if (!content) return null;
  
  const lines = content.split('\n');
  const sections = [];
  let currentSection = null;
  
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    
    // Detect headings (## or ### or **heading** or emoji-prefixed headings)
    const isHeading = trimmed.startsWith('##') || trimmed.startsWith('###') || 
                      (trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length < 80) ||
                      /^[📊📅📆✨❌🔁💰📦🚨🎯📈🧍💬📌⚠️🛒📩💡🌞💅✅👤🚫📞💷⏱🔥💎🏆]/.test(trimmed);
    
    if (isHeading) {
      const cleanTitle = trimmed.replace(/^#+\s*/, '').replace(/\*\*/g, '').trim();
      currentSection = { title: cleanTitle, items: [] };
      sections.push(currentSection);
    } else if (currentSection) {
      currentSection.items.push(trimmed);
    } else {
      // No section yet, create default
      currentSection = { title: '', items: [trimmed] };
      sections.push(currentSection);
    }
  }
  
  if (sections.length === 0) {
    return <p className="text-sm text-muted-foreground">{content}</p>;
  }
  
  return (
    <div className={`space-y-3 ${compact ? '' : 'space-y-4'}`}>
      {sections.map((section, i) => {
        const iconInfo = section.title ? getIconForHeading(section.title) : null;
        const Icon = iconInfo?.icon || Sparkles;
        
        // Title-only section (main heading)
        if (section.title && section.items.length === 0) {
          return (
            <div key={i} className="flex items-center gap-2 pt-1">
              <Icon size={18} className={iconInfo?.color || 'text-primary'} />
              <h3 className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }}>
                {section.title.replace(/^[📊📅📆✨❌🔁💰📦🚨🎯📈🧍💬📌⚠️🛒📩💡🌞💅✅👤🚫📞💷⏱🔥💎🏆]\s*/, '')}
              </h3>
            </div>
          );
        }
        
        // Section with content
        return (
          <div key={i} className={`rounded-xl border p-3 ${iconInfo?.bg || 'bg-muted/30 border-border'} ${compact ? 'p-2.5' : 'p-3.5'}`}>
            {section.title && (
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className={iconInfo?.color || 'text-primary'} />
                <h4 className={`font-semibold ${compact ? 'text-xs' : 'text-sm'}`} style={{ fontFamily: 'Playfair Display' }}>
                  {section.title.replace(/^[📊📅📆✨❌🔁💰📦🚨🎯📈🧍💬📌⚠️🛒📩💡🌞💅✅👤🚫📞💷⏱🔥💎🏆]\s*/, '')}
                </h4>
              </div>
            )}
            <div className={`space-y-1 ${compact ? 'text-xs' : 'text-sm'}`}>
              {section.items.map((item, j) => {
                const isBullet = item.startsWith('- ') || item.startsWith('* ') || item.startsWith('• ') || /^\d+\.\s/.test(item);
                const cleanItem = item.replace(/^[-*•]\s*/, '').replace(/^\d+\.\s*/, '');
                
                if (isBullet) {
                  return (
                    <div key={j} className="flex items-start gap-2 pl-1">
                      <span className="text-primary mt-0.5 shrink-0">
                        {cleanItem.toLowerCase().includes('warning') || cleanItem.toLowerCase().includes('low stock') || cleanItem.toLowerCase().includes('alert') 
                          ? <AlertTriangle size={12} className="text-amber-500" />
                          : cleanItem.toLowerCase().includes('reach out') || cleanItem.toLowerCase().includes('remind')
                          ? <Phone size={12} className="text-blue-500" />
                          : <CheckCircle size={12} />
                        }
                      </span>
                      <span className="text-foreground/80" dangerouslySetInnerHTML={{ 
                        __html: sanitizeAndFormat(cleanItem)
                      }} />
                    </div>
                  );
                }
                
                // Regular paragraph
                return (
                  <p key={j} className="text-foreground/80" dangerouslySetInnerHTML={{
                    __html: sanitizeAndFormat(item)
                  }} />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Chat message renderer with rich formatting for AI responses
 */
export function AIChatBubble({ content }) {
  // Parse the response into formatted sections
  const lines = content.split('\n');
  
  return (
    <div className="space-y-2 text-sm">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={i} className="h-1" />;
        
        // Heading with emoji
        if (/^[📊📅📆✨❌🔁💰📦🚨🎯📈🧍💬📌⚠️🛒📩💡🌞💅✅👤🚫📞💷⏱🔥💎🏆]/.test(trimmed) && trimmed.length < 60) {
          return <p key={i} className="font-semibold text-sm mt-2 mb-0.5" style={{ fontFamily: 'Playfair Display' }}>{trimmed}</p>;
        }
        
        // Bold heading
        if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
          return <p key={i} className="font-semibold mt-2 mb-0.5">{trimmed.replace(/\*\*/g, '')}</p>;
        }
        
        // Markdown heading
        if (trimmed.startsWith('##')) {
          return <p key={i} className="font-semibold mt-2 mb-0.5">{trimmed.replace(/^#+\s*/, '')}</p>;
        }
        
        // Bullet with emoji or dash
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('• ')) {
          const clean = trimmed.replace(/^[-*•]\s*/, '');
          return (
            <div key={i} className="flex items-start gap-1.5 pl-1">
              <ArrowRight size={10} className="text-primary mt-1 shrink-0" />
              <span dangerouslySetInnerHTML={{
                __html: sanitizeAndFormat(clean)
              }} />
            </div>
          );
        }
        
        // Price/duration highlight line
        if ((trimmed.includes('£') || trimmed.includes('⏱') || trimmed.includes('💷')) && trimmed.length < 40) {
          return <p key={i} className="font-medium text-primary">{trimmed}</p>;
        }
        
        // Regular text
        return (
          <p key={i} dangerouslySetInnerHTML={{
            __html: sanitizeAndFormat(trimmed)
          }} />
        );
      })}
    </div>
  );
}
